import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Star, Flame, ArrowUpRight, Clock, Heart } from "@phosphor-icons/react";

const MOODS = [
  { id: "all", label: "All", keywords: [] },
  { id: "Chippy & Takeaway", label: "Takeaway", keywords: ["chippy", "chip shop", "kebab", "takeaway"] },
  { id: "Pizza & Pasta", label: "Pizza", keywords: ["pizza", "pasta", "italian"] },
  { id: "Indian Cuisine", label: "Indian", keywords: ["indian"] },
  { id: "Grill & Steaks", label: "Grill", keywords: ["grill", "steak", "burger"] },
  { id: "Coffee & Brunch", label: "Coffee", keywords: ["coffee", "breakfast", "brunch"] },
  { id: "Grocery & Off-Sales", label: "Groceries", keywords: ["convenience", "off-sales", "grocery"] },
];

function LovedTag() {
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full bg-white text-[#FF1F8B] border border-[#FF1F8B]/20">
      <Heart size={10} weight="fill" />
      Loved locally
    </span>
  );
}

export default function PopularTonight({ businesses = [] }) {
  const [mood, setMood] = useState("all");

  const filtered = useMemo(() => {
    if (mood === "all") return businesses;
    const active = MOODS.find((m) => m.id === mood);
    if (!active) return businesses;
    return businesses.filter((b) => {
      const haystack = `${b.category} ${b.name}`.toLowerCase();
      return active.keywords.some((k) => haystack.includes(k));
    });
  }, [mood, businesses]);

  const list = filtered.length ? filtered : businesses;

  return (
    <section
      id="popular"
      data-testid="popular-tonight-section"
      className="relative bg-[#FAFAFA] pt-8 md:pt-10 pb-14 md:pb-20"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <div className="max-w-2xl">
            <div className="mb-4 flex items-center gap-2 flex-wrap">
              <span className="overline text-zinc-500 flex items-center gap-2">
                <Flame size={14} weight="fill" className="text-[#FF1F8B]" />
                What locals are ordering
              </span>
            </div>
            <h2 className="font-display font-extrabold text-zinc-900 text-4xl md:text-6xl tracking-tighter leading-[0.95]">
              On the go <span className="gradient-text">tonight.</span>
            </h2>
          </div>
          <a
            href="https://www.mylocalapp.shop/"
            target="_blank"
            rel="noreferrer"
            data-testid="see-all-popular"
            className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-900 hover:gap-3 transition-all shrink-0"
          >
            Find your local
            <ArrowUpRight size={16} weight="bold" />
          </a>
        </div>

        {/* Mood chips */}
        <div
          data-testid="mood-picker"
          className="flex gap-2 mb-8 overflow-x-auto pb-2 -mx-6 px-6 md:mx-0 md:px-0"
          style={{ scrollbarWidth: "none" }}
        >
          {MOODS.map((m) => {
            const active = m.id === mood;
            return (
              <button
                key={m.id}
                onClick={() => setMood(m.id)}
                data-testid={`mood-${m.id.replace(/\s+|&|\//g, "-")}`}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-all border ${
                  active
                    ? "brand-gradient text-white border-transparent shadow-md shadow-[#FF1F8B]/20"
                    : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300"
                }`}
              >
                {m.label}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {list.map((b, i) => {
            const loved = (b.rating ?? 0) >= 4.8;
            const featured = i === 0;
            return (
              <motion.a
                key={b.id}
                href="https://www.mylocalapp.shop/"
                target="_blank"
                rel="noreferrer"
                data-testid={`popular-card-${b.id}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.06, ease: [0.22, 1, 0.36, 1] }}
                className={`card-hover group relative block bg-white rounded-3xl overflow-hidden border border-zinc-100 hover:-translate-y-1 hover:shadow-2xl hover:shadow-[#FF1F8B]/10 hover:border-transparent transition-all duration-500 ${
                  featured ? "sm:col-span-2 lg:col-span-2 lg:row-span-2" : ""
                }`}
              >
                <div className={`relative overflow-hidden bg-zinc-100 ${featured ? "aspect-[16/10] lg:aspect-[16/11]" : "aspect-[5/4]"}`}>
                  <img
                    src={b.image_url}
                    alt={b.name}
                    loading={featured ? "eager" : "lazy"}
                    className="card-img absolute inset-0 h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  <div className="absolute top-4 left-4 flex flex-wrap items-center gap-2">
                    {b.tag && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full brand-gradient text-white shadow-md shadow-[#FF1F8B]/30">
                        <Flame size={10} weight="fill" />
                        {b.tag}
                      </span>
                    )}
                    {loved && <LovedTag />}
                  </div>
                  <div className="absolute bottom-4 right-4 flex flex-col items-end gap-2">
                    <div className="inline-flex items-center gap-1 bg-white/95 backdrop-blur rounded-full px-3 py-1.5 text-sm font-semibold text-zinc-900 shadow-sm">
                      <Star size={14} weight="fill" className="text-[#FF1F8B]" />
                      {b.rating?.toFixed(1) ?? "4.8"}
                    </div>
                  </div>
                  {featured && (
                    <div className="absolute bottom-4 left-4 text-white max-w-md">
                      <h3 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight">
                        {b.name}
                      </h3>
                      <p className="text-sm md:text-base text-white/80 mt-1">
                        {b.category} · Ready in {b.eta_mins || "45-60 min"}
                      </p>
                    </div>
                  )}
                </div>
                {!featured && (
                  <div className="p-5 md:p-6 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h3 className="font-display font-extrabold text-xl md:text-2xl text-zinc-900 tracking-tight truncate">
                        {b.name}
                      </h3>
                      <p className="text-sm text-zinc-500 mt-1">{b.category}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-xs text-zinc-400 uppercase tracking-wider">Ready in</p>
                      <p className="text-sm font-semibold text-zinc-900 inline-flex items-center gap-1 mt-0.5">
                        <Clock size={13} weight="duotone" />
                        {b.eta_mins || "45-60 min"}
                      </p>
                    </div>
                  </div>
                )}
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
